import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';

import uniqueId from 'lodash.uniqueid';
import {parseErrorMessages} from 'src/contexts/message-context/message-context-provider';
import {ChangesInfo} from 'src/modules/profile/account/tabs/admin-account-api-v2/components/changes-info';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import BoxLoading from '@pb/kit-component-box-loading';
import {FormattedDate} from '@pb/kit-component-formatters';
import {Group, Item} from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';
import {LocaleContext} from '@pb/kit-context-locale';


import {ReleaseNoteInput} from './release-note-input';
import {ReleaseNoteItem} from './release-note-item';

import {
    apiV2ReleaseNotesEditableItem,
    apiV2ReleaseNotesRemovableItem,
} from './release-notes.module.styl';

import type {ChangesInfo as ChangesType} from 'src/openapi/admin/models/ChangesInfo';
import type {Error} from 'src/openapi/admin/models/Error';
import type {ReleaseNote} from 'src/openapi/admin/models/ReleaseNote';

type ReleaseNoteWIthEditInfo = {
    isEdited?: boolean,
} & ReleaseNote;

type Props = {
    releaseNotes?: Array<ReleaseNoteWIthEditInfo>,
    error: Error | null,
    onCloseError: () => void,
    changesInfo?: ChangesType,
    onRemoveNote: (releaseNoteId: number) => void,
    onSaveNote: (releaseNoteData: ReleaseNote) => void,
    onUpdateNote: (releaseNoteData: ReleaseNote) => void,
    loading: boolean,
    canEdit: boolean,
};

export const ReleaseNotesView = (props: Props) => {

    const {
        releaseNotes,
        error,
        onCloseError,
        changesInfo,
        onRemoveNote,
        onSaveNote,
        onUpdateNote,
        loading,
        canEdit,
    } = props;

    const [notes, setNotes] = useState<Array<ReleaseNoteWIthEditInfo>>([]);
    const {locale} = useContext(LocaleContext);
    const {onCreateNote, onCloseNote} = usePushNotification();
    const errorNoteIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (releaseNotes) {
            const formattedReleaseNotes = releaseNotes.map(note => {
                return {
                    ...note,
                    releaseDate: FormattedDate.format({
                        value: note.releaseDate,
                        format: mapDateFormat(locale.dateFormat, 'WORD'),
                    }) || '',
                };
            });
            setNotes(formattedReleaseNotes);
        }
    }, [releaseNotes, locale.dateFormat]);

    const onSave = useCallback((releaseNoteData: ReleaseNote) => {
        onSaveNote(releaseNoteData);
    }, [onSaveNote]);

    const setEdited = useCallback((id: number, isEdited: boolean) => {
        const mappedNotes = notes.map(note => {
            if (note.id === id) {
                return ({
                    ...note,
                    isEdited,
                });
            }
            return note;
        });
        setNotes(mappedNotes);
    }, [notes]);

    const editNote = useCallback((id: number) => {
        setEdited(id, true);
    }, [setEdited]);

    const cancelEditNote = useCallback((id: number) => {
        setEdited(id, false);
    }, [setEdited]);

    const noteItems = useMemo(() => notes
        .map(item => {
            if (item.isEdited) {
                return (
                    <Item key={item.id} className={`${apiV2ReleaseNotesEditableItem} pt-s`}>
                        <ReleaseNoteInput
                            onUpdate={onUpdateNote}
                            onCancel={cancelEditNote}
                            canEdit={canEdit}
                            {...item}
                        />
                    </Item>
                );
            }
            return (
                <Item className={apiV2ReleaseNotesRemovableItem} key={item.id}>
                    <ReleaseNoteItem
                        {...{...item, id: item.id || -1, editNote, onRemoveNote, loading, canEdit}}
                    />
                </Item>
            );
        }), [notes, onRemoveNote, cancelEditNote, editNote, onUpdateNote, loading, canEdit]);

    useEffect(() => {
        if (!error) {
            if (errorNoteIdRef.current) {
                onCloseNote(errorNoteIdRef.current);
                errorNoteIdRef.current = null;
            }
            return;
        }

        if (errorNoteIdRef.current) {
            onCloseNote(errorNoteIdRef.current);
        }

        const noteId = uniqueId('admin-account-api-release-notes-error-');
        errorNoteIdRef.current = noteId;
        onCreateNote({
            id: noteId,
            status: Status.ERROR,
            buttonProps: {
                iconProps: {type: Icons.CLOSE},
                onClick: () => {
                    onCloseError();
                },
            },
            body: () => parseErrorMessages(error?.message || ''),
        });

        return () => {
            if (errorNoteIdRef.current) {
                onCloseNote(errorNoteIdRef.current);
                errorNoteIdRef.current = null;
            }
        };
    }, [error, onCloseError, onCreateNote, onCloseNote]);


    return (
        <>
            <BoxLoading loading={loading}>
                <Group direction="v" gap="xl">
                    <Group gap="none">
                        <Group direction="v">
                            <Group justify="start">
                                <Item style={{width: 100}}>
                                    <Label>Version</Label>
                                </Item>
                                <Item style={{width: 150}}>
                                    <Label>Date</Label>
                                </Item>
                                <Item style={{width: 100}}>
                                    <Label>Scope</Label>
                                </Item>
                                <Item grow={1}>
                                    <Label>Details</Label>
                                </Item>
                            </Group>
                            <Group direction="v">
                                {noteItems}
                            </Group>
                            <Item className="pt-l">
                                <ReleaseNoteInput onSave={onSave} canEdit={canEdit}/>
                            </Item>
                        </Group>
                    </Group>
                    {changesInfo ? (
                        <Group>
                            <ChangesInfo data={changesInfo}/>
                        </Group>
                    ) : null}
                </Group>
            </BoxLoading>
        </>
    );
};
